// Illustrative solar-geometry helpers for the calendar curve preview.
//
// IMPORTANT: this is a deliberately simplified, *representative-day* model used
// only to visualise how the zenith calendar behaves around sunrise/sunset. It
// uses the EQUINOX approximation (solar declination δ ≈ 0) and ignores
// longitude, the equation of time, and the season. A full ephemeris (real δ for
// the chosen date, longitude/timezone, atmospheric refraction) is a future
// refinement — the device firmware uses a real solar model; this front-end
// preview only needs to be qualitatively correct.

import { CalendarStep } from '../types/dimmingProfile.types'

const DEG2RAD = Math.PI / 180

// Solar elevation angle α (deg) above the horizon for latitude φ (deg) at local
// solar time t (hours, 0..24) on the EQUINOX (δ = 0).
//
// Hour angle H = 15°·(t − 12). With δ = 0:
//   sin(α) = cos(φ)·cos(H)
// (the general formula sin α = sinφ·sinδ + cosφ·cosδ·cosH collapses because
// sinδ = 0 and cosδ = 1). α is negative when the sun is below the horizon.
export function solarElevationDeg(latitudeDeg: number, tHours: number): number {
  const hourAngleDeg = 15 * (tHours - 12)
  const sinAlpha =
    Math.cos(latitudeDeg * DEG2RAD) * Math.cos(hourAngleDeg * DEG2RAD)
  // Clamp for floating-point safety before asin.
  const clamped = Math.max(-1, Math.min(1, sinAlpha))
  return Math.asin(clamped) / DEG2RAD
}

// Solar zenith angle θ (deg) = 90 − elevation. θ = 90° is the horizon; θ > 90°
// means the sun is below the horizon (twilight / night).
export function solarZenithDeg(latitudeDeg: number, tHours: number): number {
  return 90 - solarElevationDeg(latitudeDeg, tHours)
}

// Inverse of solarZenithDeg: the local solar time (hours) at which the sun
// reaches zenith θ on a given half of the representative (equinox) day. Same
// simplified-model caveat as solarZenithDeg (δ = 0, no longitude/season).
//
// Derivation (δ = 0): sin(α) = cos(φ)·cos(H) with α = 90 − θ and
// H = 15°·(t − 12). So cos(H) = sin((90 − θ)°)/cos(φ°). When cos(φ) ≤ 0 (|φ| ≥
// 90°) or |cos(H)| > 1 (the zenith is never reached at this latitude) we return
// null. Otherwise H = acos(clamp(cos(H))) ∈ [0°, 180°] and
//   am → t = 12 − H/15, pm → t = 12 + H/15.
// Sanity: θ = 90 → H = 90 → am = 6 / pm = 18; θ = |φ| → H = 0 → 12;
// θ = 110 (φ small) → pm ≈ 19.7.
export function solarTimeForZenith(
  latitudeDeg: number,
  zenithDeg: number,
  half: 'am' | 'pm',
): number | null {
  const cosPhi = Math.cos(latitudeDeg * DEG2RAD)
  if (cosPhi <= 0) return null // |φ| ≥ 90°: degenerate
  const cosH = Math.sin((90 - zenithDeg) * DEG2RAD) / cosPhi
  if (cosH > 1 || cosH < -1) return null // zenith unreachable at this latitude
  const hDeg = Math.acos(cosH) / DEG2RAD // 0..180
  return half === 'am' ? 12 - hDeg / 15 : 12 + hDeg / 15
}

// Solar noon zenith (the minimum zenith of the representative day) for latitude
// φ. On the equinox the sun culminates at elevation (90 − |φ|), so the minimum
// zenith equals |φ|. Used to decide whether the sun ever rises ("polar night").
export function minZenithDeg(latitudeDeg: number): number {
  return Math.abs(latitudeDeg)
}

// Find the local solar times (hours) where the zenith crosses the horizon
// (θ = 90°, i.e. elevation = 0) for the representative day. Returns
// { sunrise, sunset } in hours, or null when the sun never crosses the horizon
// at this latitude on the equinox (|φ| ≥ 90 → degenerate). On the equinox these
// are symmetric around solar noon (12:00): sunrise ≈ 06:00, sunset ≈ 18:00 for
// mid-latitudes, drifting toward the poles.
//
// Solving cos(φ)·cos(H) = 0 with cos(φ) ≠ 0 gives H = ±90°, i.e.
// 15·(t − 12) = ±90 → t = 12 ∓ 6 = 06:00 / 18:00 regardless of latitude on the
// equinox. We still compute it numerically so the helper stays correct if the
// model is later refined with a real declination.
export function sunHorizonTimes(
  latitudeDeg: number,
): { sunrise: number; sunset: number } | null {
  const cosPhi = Math.cos(latitudeDeg * DEG2RAD)
  if (cosPhi <= 0) return null // |φ| ≥ 90°: degenerate
  // cos(H) = 0 → H = ±90° → t = 12 ± 6.
  return { sunrise: 6, sunset: 18 }
}

// ── Annual (seasonal) helpers ────────────────────────────────────────────────
// These extend the equinox-only model above to a full year by introducing a
// simplified solar declination δ(dayOfYear). They remain a deliberately
// simplified astronomical model: no equation of time, no atmospheric
// refraction, and δ uses the common sinusoidal approximation. This is enough to
// visualise how the solar-noon elevation/zenith drifts across the seasons.

// Solar declination δ (deg) for a day of the year (1..365), using the standard
// sinusoidal approximation that peaks at the June solstice (δ ≈ +23.44°) and
// troughs at the December solstice (δ ≈ −23.44°). Day 81 (≈ Mar 21, the
// equinox) yields δ ≈ 0, consistent with the equinox model above.
export function solarDeclinationDeg(dayOfYear: number): number {
  return 23.44 * Math.sin((360 / 365) * (dayOfYear - 81) * DEG2RAD)
}

// Maximum (solar-noon) elevation α (deg) above the horizon for latitude φ on a
// given day of the year. At solar noon the sun culminates at elevation
// 90 − |φ − δ|. Clamped to ≥ 0 (polar night → the sun never rises).
export function maxSolarElevationDeg(
  latitudeDeg: number,
  dayOfYear: number,
): number {
  const decl = solarDeclinationDeg(dayOfYear)
  return Math.max(0, 90 - Math.abs(latitudeDeg - decl))
}

// Solar-noon zenith angle θ (deg) for latitude φ on a given day of the year.
// θ = |φ − δ| (the complement of the noon elevation). Smaller θ = sun higher.
export function noonZenithDeg(
  latitudeDeg: number,
  dayOfYear: number,
): number {
  return Math.abs(latitudeDeg - solarDeclinationDeg(dayOfYear))
}

// Resolve the dimming level (%) for a given solar zenith θ from an ordered list
// of zenith steps, as the sun crosses/approaches the horizon.
//
// Each step is an active threshold: it engages once the sun has reached that
// zenith (θ ≥ step.zenith_angle_deg). We sort the steps by ascending zenith and
// pick the level of the LAST step whose threshold the current zenith has reached
// (deepest twilight reached). If the sun is still high (θ below every
// threshold) → 0% (full daylight). Once θ passes the last (largest) threshold we
// hold that last step's level (deep night).
//
// Shared between the daily CalendarCurve and the annual AnnualZenithCurve so the
// mapping from zenith → level stays single-sourced.
export function levelForZenith(
  zenithDeg: number,
  steps: CalendarStep[],
): number {
  if (steps.length === 0) return 0
  const sorted = [...steps].sort(
    (a, b) => a.zenith_angle_deg - b.zenith_angle_deg,
  )
  let level = 0 // default: daylight, before the first threshold is reached
  for (const s of sorted) {
    if (zenithDeg >= s.zenith_angle_deg) {
      level = s.dimming_level_percent // threshold reached → adopt this level
    } else {
      break // sorted ascending: no further threshold can be reached
    }
  }
  return level
}
